import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type PermissionModule =
  | 'pos_screen'
  | 'product_management'
  | 'stock_management'
  | 'cancel_transaction'
  | 'reorder'
  | 'loyalty'
  | 'marketing'
  | 'reports'
  | 'finance'
  | 'team_management'
  | 'customers'
  | 'settings';

export const MODULE_LABELS: Record<PermissionModule, string> = {
  pos_screen: 'Écran de caisse',
  product_management: 'Gestion des articles',
  stock_management: 'Gestion des stocks',
  cancel_transaction: 'Annuler une transaction',
  reorder: 'Réassort',
  loyalty: 'Fidélité',
  marketing: 'Marketing',
  reports: 'Rapports',
  finance: 'Finances',
  team_management: 'Équipe',
  customers: 'Clients',
  settings: 'Paramètres',
};

export const MODULE_ORDER: PermissionModule[] = [
  'pos_screen',
  'product_management',
  'stock_management',
  'cancel_transaction',
  'reorder',
  'loyalty',
  'marketing',
  'reports',
  'finance',
  'team_management',
  'customers',
  'settings',
];

export interface UserPermission {
  module: PermissionModule;
  allowed: boolean;
  is_override: boolean;
}

/**
 * Hook to get and manage permissions for the current user.
 * Uses the RPC get_user_permissions for batch loading.
 */
export function useMyPermissions() {
  const { user, tenantId } = useAuth();
  const [permissions, setPermissions] = useState<Record<PermissionModule, boolean>>({} as any);
  const [loading, setLoading] = useState(true);
  /** true uniquement après un chargement réussi (un échec RPC ne doit pas masquer le menu). */
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user?.id || !tenantId) return;
    loadPermissions();
  }, [user?.id, tenantId]);

  const loadPermissions = async () => {
    if (!user?.id || !tenantId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_user_permissions', {
        p_user_id: user.id,
        p_tenant_id: tenantId,
      });
      if (error) throw error;
      const perms: Record<string, boolean> = {};
      (data || []).forEach((row: any) => {
        perms[row.module] = row.allowed;
      });
      setPermissions(perms as Record<PermissionModule, boolean>);
      setLoaded(true);
    } catch (err) {
      console.error('Error loading permissions:', err);
    }
    setLoading(false);
  };

  const can = useCallback(
    (module: PermissionModule): boolean => {
      return permissions[module] ?? false;
    },
    [permissions],
  );

  return { permissions, loading, loaded, can, reload: loadPermissions };
}

/**
 * Hook for admin to manage another user's permissions.
 */
export function useManagePermissions(targetUserId: string, tenantId: string) {
  const [permissions, setPermissions] = useState<UserPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (targetUserId && tenantId) loadPermissions();
  }, [targetUserId, tenantId]);

  const loadPermissions = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_user_permissions', {
        p_user_id: targetUserId,
        p_tenant_id: tenantId,
      });
      if (error) throw error;
      setPermissions(
        (data || []).map((row: any) => ({
          module: row.module as PermissionModule,
          allowed: row.allowed,
          is_override: row.is_override,
        })),
      );
    } catch (err) {
      console.error('Error loading permissions:', err);
    }
    setLoading(false);
  };

  const togglePermission = async (module: PermissionModule, allowed: boolean) => {
    setSaving(true);
    try {
      // Upsert the permission override
      const { error } = await supabase.from('user_permissions' as any).upsert(
        {
          user_id: targetUserId,
          tenant_id: tenantId,
          module,
          allowed,
        },
        { onConflict: 'user_id,tenant_id,module' },
      );
      if (error) throw error;
      // Update local state
      setPermissions((prev) =>
        prev.map((p) => (p.module === module ? { ...p, allowed, is_override: true } : p)),
      );
    } catch (err) {
      console.error('Error toggling permission:', err);
    }
    setSaving(false);
  };

  const resetPermission = async (module: PermissionModule) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('user_permissions' as any)
        .delete()
        .eq('user_id', targetUserId)
        .eq('tenant_id', tenantId)
        .eq('module', module);
      if (error) throw error;
      await loadPermissions(); // Reload to get role defaults
    } catch (err) {
      console.error('Error resetting permission:', err);
    }
    setSaving(false);
  };

  return {
    permissions,
    loading,
    saving,
    togglePermission,
    resetPermission,
    reload: loadPermissions,
  };
}
